// Sub-build 2b — translates qualitative magnitude bands into cm offsets.
// Mirrored at supabase/functions/_shared/magnitudeTranslation.ts.

import type { FashionAIFitType } from "./fitTypeNormalisation";

export type MagnitudeBand =
  | "slightly"
  | "moderately"
  | "significantly"
  | "dramatically";

const FIT_TYPE_DIRECTION: Record<FashionAIFitType, "positive" | "negative"> = {
  slim: "negative",
  relaxed: "positive",
  oversized: "positive",
  cropped: "negative",
  longline: "positive",
  muscle: "negative",
};

const SLIM_DIRECTION_CHEST_CLAMP = -10;
const SLIM_DIRECTION_WAIST_CLAMP = -10;

type BandData = {
  chest_cm_range: [number, number];
  waist_cm_range: [number, number];
  length_cm_range: [number, number];
};

export type MagnitudeMapping = Record<MagnitudeBand, BandData> & {
  // tolerant to extra fields like `interpretation`
  [key: string]: unknown;
};

export type CmOffset = {
  chest_cm_min: number;
  chest_cm_max: number;
  chest_cm_midpoint: number;
  waist_cm_min: number;
  waist_cm_max: number;
  waist_cm_midpoint: number;
  length_cm_min: number;
  length_cm_max: number;
  length_cm_midpoint: number;
};

export function magnitudeToOffset(
  fitType: FashionAIFitType,
  magnitude: MagnitudeBand,
  mapping: MagnitudeMapping,
): CmOffset {
  const direction = FIT_TYPE_DIRECTION[fitType];
  const sign = direction === "positive" ? 1 : -1;
  const band = mapping[magnitude] as BandData;

  let chestMin = sign * band.chest_cm_range[0];
  let chestMax = sign * band.chest_cm_range[1];
  let waistMin = sign * band.waist_cm_range[0];
  let waistMax = sign * band.waist_cm_range[1];
  let lengthMin = sign * band.length_cm_range[0];
  let lengthMax = sign * band.length_cm_range[1];

  // Cropped/longline affect length only — zero out chest/waist.
  if (fitType === "cropped" || fitType === "longline") {
    chestMin = 0;
    chestMax = 0;
    waistMin = 0;
    waistMax = 0;
  } else {
    // Other fit types don't affect length — zero out length.
    lengthMin = 0;
    lengthMax = 0;
  }

  // Asymmetry clamp for slim: cap most-negative at -10cm.
  if (fitType === "slim") {
    chestMin = Math.max(chestMin, SLIM_DIRECTION_CHEST_CLAMP);
    chestMax = Math.max(chestMax, SLIM_DIRECTION_CHEST_CLAMP);
    waistMin = Math.max(waistMin, SLIM_DIRECTION_WAIST_CLAMP);
    waistMax = Math.max(waistMax, SLIM_DIRECTION_WAIST_CLAMP);
  }

  const cMin = Math.min(chestMin, chestMax);
  const cMax = Math.max(chestMin, chestMax);
  const wMin = Math.min(waistMin, waistMax);
  const wMax = Math.max(waistMin, waistMax);
  const lMin = Math.min(lengthMin, lengthMax);
  const lMax = Math.max(lengthMin, lengthMax);

  return {
    chest_cm_min: cMin,
    chest_cm_max: cMax,
    chest_cm_midpoint: (cMin + cMax) / 2,
    waist_cm_min: wMin,
    waist_cm_max: wMax,
    waist_cm_midpoint: (wMin + wMax) / 2,
    length_cm_min: lMin,
    length_cm_max: lMax,
    length_cm_midpoint: (lMin + lMax) / 2,
  };
}
